import { useState } from 'react';
import {
  AutoComplete,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Switch,
  Table,
  Tag,
  Tooltip,
  Upload,
  message,
  Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/useAuthStore';
import { useFilUpload } from '../../Hooks/Index';
import {
  useCreateEvVehicleModel,
  useDeleteEvVehicleModel,
  useFetchEvVehicleBrands,
  useFetchEvVehicleModels,
  useUpdateEvVehicleModel,
} from './Hooks';

const { Title } = Typography;

const emptyForm = {
  name: '',
  brand: '',
  model: '',
  bodyType: '',
  usableBatteryKwh: null,
  realRangeKm: null,
  acPort: '',
  dcPort: '',
  acMaxKw: null,
  dcMaxKw: null,
  imageUrl: '',
  brandLogoUrl: '',
  isDefault: false,
};

const toFileList = (url, name) =>
  url
    ? [
        {
          uid: `-${name}`,
          name,
          status: 'done',
          url,
        },
      ]
    : [];

export default function EvVehicleModelsPage() {
  const { t } = useTranslation('evVehicleModels');
  const { user } = useAuthStore();
  const isReadOnly = user?.role === 'READ_ONLY_ADMIN';
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modelImageList, setModelImageList] = useState([]);
  const [brandLogoList, setBrandLogoList] = useState([]);
  const [form] = Form.useForm();
  const { mutateAsync: uploadFile } = useFilUpload();

  const { data, isLoading, refetch } = useFetchEvVehicleModels({
    page,
    search,
    brand,
    limit: 20,
  });
  const { data: brands = [] } = useFetchEvVehicleBrands();
  const { mutate: createModel, isPending: creating } = useCreateEvVehicleModel({
    onSuccess: () => {
      message.success(t('createSuccess'));
      closeDrawer();
      refetch();
    },
    onError: (err) => message.error(err?.response?.data?.message || 'Error'),
  });
  const { mutate: updateModel, isPending: updating } = useUpdateEvVehicleModel({
    onSuccess: () => {
      message.success(t('updateSuccess'));
      closeDrawer();
      refetch();
    },
    onError: (err) => message.error(err?.response?.data?.message || 'Error'),
  });
  const { mutate: deleteModel } = useDeleteEvVehicleModel({
    onSuccess: () => {
      message.success(t('deleteSuccess'));
      refetch();
    },
    onError: (err) => message.error(err?.response?.data?.message || 'Error'),
  });

  const models = data?.paginatedData || [];
  const totalCount = data?.totalCount || 0;
  const pageSize = data?.itemsPerPage || 20;

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    setModelImageList([]);
    setBrandLogoList([]);
    form.resetFields();
  };

  const openCreate = () => {
    setEditing(null);
    setModelImageList([]);
    setBrandLogoList([]);
    form.setFieldsValue(emptyForm);
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setModelImageList(toFileList(record.imageUrl, 'model-image'));
    setBrandLogoList(toFileList(record.brandLogoUrl, 'brand-logo'));
    form.setFieldsValue({
      name: record.name,
      brand: record.brand,
      model: record.model,
      bodyType: record.bodyType,
      usableBatteryKwh: record.usableBatteryKwh,
      realRangeKm: record.realRangeKm,
      acPort: record.acPort,
      dcPort: record.dcPort,
      acMaxKw: record.acMaxKw,
      dcMaxKw: record.dcMaxKw,
      imageUrl: record.imageUrl || '',
      brandLogoUrl: record.brandLogoUrl || '',
      isDefault: !!record.isDefault,
    });
    setDrawerOpen(true);
  };

  const makeUploadHandler = (field, setList) => async ({ file, onSuccess, onError }) => {
    try {
      setList([
        {
          uid: file.uid,
          name: file.name,
          status: 'uploading',
        },
      ]);
      const result = await uploadFile(file);
      const url = result?.Location || '';
      setList([
        {
          uid: file.uid,
          name: file.name,
          status: 'done',
          url,
        },
      ]);
      form.setFieldsValue({ [field]: url });
      onSuccess?.(result, file);
      message.success(t('uploadSuccess'));
    } catch (error) {
      setList([
        {
          uid: file.uid,
          name: file.name,
          status: 'error',
        },
      ]);
      onError?.(error);
      message.error(
        error?.response?.data?.message ||
          error?.response?.data?.status_message ||
          t('uploadFailed')
      );
    }
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    if (editing?._id) {
      updateModel({ id: editing._id, ...values });
    } else {
      createModel(values);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: t('deleteConfirm'),
      okText: t('delete'),
      cancelText: t('cancel'),
      okButtonProps: { danger: true },
      onOk: () => deleteModel(record._id),
    });
  };

  const columns = [
    {
      title: t('brand'),
      dataIndex: 'brand',
      key: 'brand',
      width: 160,
      render: (value, record) => (
        <span className="inline-flex items-center gap-2 min-w-0">
          {record.brandLogoUrl ? (
            <img
              src={record.brandLogoUrl}
              alt={value}
              className="h-5 w-5 object-contain shrink-0"
            />
          ) : null}
          <span className="truncate">{value || '—'}</span>
        </span>
      ),
    },
    {
      title: t('modelImage'),
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 88,
      align: 'center',
      render: (url, record) =>
        url ? (
          <img
            src={url}
            alt={record.name || record.model}
            className="h-10 w-14 object-contain mx-auto"
          />
        ) : (
          <span className="text-xs text-gray-400">{t('noImage')}</span>
        ),
    },
    {
      title: t('model'),
      dataIndex: 'model',
      key: 'model',
      ellipsis: true,
      render: (value, record) => (
        <div className="min-w-0">
          <div className="font-medium truncate">{record.name || value || '—'}</div>
          {record.name && value ? (
            <div className="text-xs text-gray-500 truncate">{value}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: t('battery'),
      dataIndex: 'usableBatteryKwh',
      key: 'usableBatteryKwh',
      width: 120,
      align: 'center',
      render: (value) => (value != null ? value : '—'),
    },
    {
      title: t('range'),
      dataIndex: 'realRangeKm',
      key: 'realRangeKm',
      width: 110,
      align: 'center',
      render: (value) => (value != null ? value : '—'),
    },
    {
      title: t('default'),
      dataIndex: 'isDefault',
      key: 'isDefault',
      width: 100,
      align: 'center',
      render: (value) =>
        value ? <Tag color="green">{t('default')}</Tag> : '—',
    },
    ...(!isReadOnly
      ? [
          {
            title: t('actions'),
            key: 'actions',
            width: 110,
            align: 'center',
            render: (_, record) => (
              <div className="flex items-center justify-center gap-1">
                <Tooltip title={t('editModel')}>
                  <Button
                    type="default"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEdit(record)}
                  />
                </Tooltip>
                <Tooltip title={t('delete')}>
                  <Button
                    type="default"
                    size="small"
                    danger
                    disabled={record.isDefault}
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(record)}
                  />
                </Tooltip>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input.Search
            allowClear
            placeholder={t('searchPlaceholder')}
            onSearch={(value) => {
              setPage(1);
              setSearch(value || '');
            }}
            style={{ width: 260 }}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('allBrands')}
            style={{ width: 200 }}
            value={brand || undefined}
            onChange={(value) => {
              setPage(1);
              setBrand(value || '');
            }}
            options={brands.map((item) => ({
              label: item.brand,
              value: item.brand,
            }))}
          />
        </div>
        {!isReadOnly && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t('addModel')}
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={models}
        loading={isLoading}
        rowKey="_id"
        size="small"
        variant="outlined"
        scroll={{ x: 800 }}
        locale={{ emptyText: t('empty') }}
        pagination={{
          current: page,
          pageSize,
          total: totalCount,
          position: ['bottomCenter'],
          showSizeChanger: false,
          onChange: setPage,
        }}
      />

      <Drawer
        title={
          <Title level={4} className="mb-0!">
            {editing ? t('editModel') : t('addModel')}
          </Title>
        }
        open={drawerOpen}
        onClose={closeDrawer}
        width={640}
        destroyOnClose
        styles={{ body: { padding: '20px 24px 24px' } }}
        extra={
          !isReadOnly ? (
            <Button type="primary" loading={creating || updating} onClick={onSubmit}>
              {t('save')}
            </Button>
          ) : null
        }
      >
        <Form form={form} layout="vertical" initialValues={emptyForm} disabled={isReadOnly}>
          <Form.Item name="imageUrl" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="brandLogoUrl" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="brand"
            label={t('brand')}
            rules={[{ required: true }]}
          >
            <AutoComplete
              allowClear
              options={brands.map((item) => ({
                label: item.brand,
                value: item.brand,
              }))}
              filterOption={(inputValue, option) =>
                (option?.value || '')
                  .toLowerCase()
                  .includes(inputValue.trim().toLowerCase())
              }
              placeholder={t('brand')}
            />
          </Form.Item>
          <Form.Item
            name="model"
            label={t('model')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label={t('name')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="bodyType" label={t('bodyType')}>
            <Input />
          </Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="usableBatteryKwh" label={t('battery')}>
              <InputNumber className="w-full" />
            </Form.Item>
            <Form.Item name="realRangeKm" label={t('range')}>
              <InputNumber className="w-full" />
            </Form.Item>
            <Form.Item name="acPort" label={t('acPort')}>
              <Input />
            </Form.Item>
            <Form.Item name="dcPort" label={t('dcPort')}>
              <Input />
            </Form.Item>
            <Form.Item name="acMaxKw" label={t('acMaxKw')}>
              <InputNumber className="w-full" />
            </Form.Item>
            <Form.Item name="dcMaxKw" label={t('dcMaxKw')}>
              <InputNumber className="w-full" />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item label={t('modelImage')}>
              <Upload
                listType="picture-card"
                accept=".png,.jpg,.jpeg,.webp"
                maxCount={1}
                fileList={modelImageList}
                customRequest={makeUploadHandler('imageUrl', setModelImageList)}
                onPreview={(file) => {
                  if (file.url) window.open(file.url, '_blank');
                }}
                onRemove={() => {
                  setModelImageList([]);
                  form.setFieldsValue({ imageUrl: '' });
                }}
              >
                {modelImageList.length < 1 && (
                  <button type="button" className="flex flex-col items-center gap-1 border-0 bg-transparent">
                    <PlusOutlined />
                    <span>{t('upload')}</span>
                  </button>
                )}
              </Upload>
            </Form.Item>
            <Form.Item label={t('brandLogo')}>
              <Upload
                listType="picture-card"
                accept=".png,.jpg,.jpeg,.webp"
                maxCount={1}
                fileList={brandLogoList}
                customRequest={makeUploadHandler('brandLogoUrl', setBrandLogoList)}
                onPreview={(file) => {
                  if (file.url) window.open(file.url, '_blank');
                }}
                onRemove={() => {
                  setBrandLogoList([]);
                  form.setFieldsValue({ brandLogoUrl: '' });
                }}
              >
                {brandLogoList.length < 1 && (
                  <button type="button" className="flex flex-col items-center gap-1 border-0 bg-transparent">
                    <PlusOutlined />
                    <span>{t('upload')}</span>
                  </button>
                )}
              </Upload>
            </Form.Item>
          </div>
          <Form.Item
            name="isDefault"
            label={t('setAsDefault')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
